import React, { useEffect, useRef, useState } from "react";
import { wikiApi } from "../../lib/api";
import { Search, FileText, X } from "lucide-react";

export default function WikiSearch({ onClose, onSelect }) {
    const [query, setQuery] = useState("");
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const inputRef = useRef(null);
    const timerRef = useRef(null);

    useEffect(() => {
        inputRef.current?.focus();
    }, []);

    useEffect(() => {
        const handler = (e) => {
            if (e.key === "Escape") onClose();
        };
        window.addEventListener("keydown", handler);
        return () => window.removeEventListener("keydown", handler);
    }, [onClose]);

    useEffect(() => {
        if (timerRef.current) clearTimeout(timerRef.current);
        const q = query.trim();
        if (q.length < 2) {
            setResults([]);
            return;
        }
        setLoading(true);
        timerRef.current = setTimeout(async () => {
            try {
                const r = await wikiApi.search(q);
                setResults(r);
            } catch {
                setResults([]);
            } finally {
                setLoading(false);
            }
        }, 250);
        return () => clearTimeout(timerRef.current);
    }, [query]);

    return (
        <div className="fixed inset-0 z-50 flex items-start justify-center pt-[15vh]">
            {/* Backdrop */}
            <div className="absolute inset-0 bg-black/40" onClick={onClose} />

            {/* Dialog */}
            <div className="relative w-full max-w-lg bg-white brut-border brut-shadow z-10">
                {/* Input */}
                <div className="flex items-center gap-2 px-4 py-3 border-b-3 border-black">
                    <Search size={18} strokeWidth={3} />
                    <input
                        ref={inputRef}
                        type="text"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Search wiki pages…"
                        className="flex-1 outline-none font-mono text-sm"
                        onKeyDown={(e) => {
                            if (e.key === "Enter" && results.length > 0) {
                                onSelect(results[0].slug);
                            }
                        }}
                    />
                    <button onClick={onClose} className="p-1">
                        <X size={16} strokeWidth={3} />
                    </button>
                </div>

                {/* Results */}
                <div className="max-h-80 overflow-y-auto">
                    {loading && (
                        <div className="px-4 py-8 text-center font-mono text-xs opacity-50">
                            Searching…
                        </div>
                    )}
                    {!loading && query.length >= 2 && results.length === 0 && (
                        <div className="px-4 py-8 text-center font-mono text-xs opacity-50">
                            No pages found for &ldquo;{query}&rdquo;
                        </div>
                    )}
                    {!loading &&
                        results.map((r) => (
                            <button
                                key={r.id}
                                onClick={() => onSelect(r.slug)}
                                className="w-full text-left px-4 py-3 hover:bg-gray-50 border-b border-gray-200 last:border-0 flex items-start gap-3"
                            >
                                <FileText
                                    size={16}
                                    strokeWidth={2.5}
                                    className="mt-0.5 opacity-40 shrink-0"
                                />
                                <div className="min-w-0">
                                    <div className="font-display text-sm">
                                        {highlightMatch(r.title, query)}
                                    </div>
                                    {r.snippet && (
                                        <div className="font-mono text-xs opacity-50 truncate mt-0.5">
                                            {highlightMatch(
                                                r.snippet.substring(0, 120),
                                                query,
                                            )}
                                        </div>
                                    )}
                                </div>
                            </button>
                        ))}
                </div>

                {/* Footer */}
                <div className="px-4 py-2 border-t-3 border-black bg-gray-50 flex items-center justify-between">
                    <span className="font-mono text-[10px] opacity-40">
                        {results.length} result{results.length !== 1 ? "s" : ""}
                    </span>
                    <span className="font-mono text-[10px] opacity-40">
                        ESC to close &middot; ENTER to open
                    </span>
                </div>
            </div>
        </div>
    );
}

function highlightMatch(text, query) {
    if (!query) return text;
    const idx = text.toLowerCase().indexOf(query.toLowerCase());
    if (idx === -1) return text;
    return (
        <>
            {text.substring(0, idx)}
            <mark className="bg-yellow-200 px-0.5">
                {text.substring(idx, idx + query.length)}
            </mark>
            {text.substring(idx + query.length)}
        </>
    );
}
